import {FunctionComponent, ReactNode, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {successMsg} from "../simpleMessageAlert";
import {Button, Input, Modal, Tooltip} from "antd";
import {EditOutlined, LoadingOutlined} from "@ant-design/icons";
import {ActionCreatorWithPayload} from "@reduxjs/toolkit";
import "./index.css";

type FieldEditProps = {
  appId: string
  editButtonAriaLabel: string
  modalTitle: string
  inputLabel: ReactNode
  inputErrorMessage: string
  savedValue: string
  isInputValid: (input: string) => boolean
  updateApi: (input: string, value: any) => Promise<any>
  actionAfterUpdate: ActionCreatorWithPayload<any>
  successMessage: string
  buttonTooltipMessage: string
}

export const FieldEdit : FunctionComponent<FieldEditProps> = (props: FieldEditProps) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [input, setInput] = useState<string>("")
  const [inputStatus, setInputStatus] = useState<"error" | "">("")
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true)
  const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false)
  const dispatch = useDispatch()

  useEffect(() => {
    setInput(props.savedValue)
    setInputStatus("")
    setIsSaveInProgress(false)
  }, [props.savedValue, isModalVisible])

  useEffect(() => {
    if (input === props.savedValue) {
      setIsSaveDisabled(true)
    } else if (!props.isInputValid(input)) {
      setInputStatus("error")
      setIsSaveDisabled(true)
    } else {
      setInputStatus("")
      setIsSaveDisabled(false)
    }
  }, [input, props.savedValue])

  const onOk = () => {
    setIsSaveInProgress(true)
    setIsSaveDisabled(true)
    props.updateApi(props.appId, input)
      .then(
        success => updateSuccessful(success),
        error => updateFailed()
      );
  }

  const updateSuccessful = (response) => {
    dispatch(props.actionAfterUpdate({...response, appId: props.appId}))
    setIsModalVisible(false)
    successMsg(props.successMessage)
  }

  const updateFailed = () => {
    setIsSaveInProgress(false)
    setIsSaveDisabled(false)
  }

  return (
    <>
      <Tooltip
        placement={"top"}
        title={<span style={{fontSize: "12px"}}>{props.buttonTooltipMessage}</span>}
        overlayClassName={"field-edit-tooltip"}
      >
        <Button
          style={{border: "none", padding: "5px"}}
          onClick={() => setIsModalVisible(true)}
          aria-label={props.editButtonAriaLabel}
        >
          <EditOutlined/>
        </Button>
      </Tooltip>
      <Modal
        className={"update-modal"}
        title={<div style={{color: "white", fontSize: "18px", fontWeight: 700}}>{props.modalTitle}</div>}
        visible={isModalVisible}
        closeIcon={<></>}
        okText={(<span>{isSaveInProgress ? <LoadingOutlined/> : null} Save</span>)}
        okButtonProps={{style: {backgroundColor: "#47A1AD"}, disabled: isSaveDisabled}}
        onOk={onOk}
        cancelButtonProps={{disabled: isSaveInProgress}}
        onCancel={() => {
          setIsModalVisible(false)
        }}
        getContainer={() => document.querySelector(".fullscreen-enabled") || document.body}
      >
        {props.inputLabel}
        <Input
          value={input}
          onChange={event => setInput(event.target.value)}
          status={inputStatus}
        />
        {(inputStatus === "error") ? <div style={{fontSize: "12px", color: "#ff4d4f"}}>{props.inputErrorMessage}</div> : null}
      </Modal>
    </>
  )
}
